import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { type Card, DeadlineBadge, type Label } from '@/entities/board';
import { useMyBoards } from '@/entities/collab';
import {
  groupTasks,
  type MyTaskBoard,
  type MyTaskPerson,
  type TaskGroup,
  TASK_GROUPS,
  taskPath,
  useMyTasks,
} from '@/entities/my-tasks';
import { useWorkspaces } from '@/entities/workspace';
import { type FilterOption, OptionFilter } from '@/features/board-filter';
import { Button, EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import {
  EMPTY_LIST_FILTER,
  type ListFilter,
  listFilterActive,
  listFilterFromParams,
  listFilterToParams,
} from './filter';
import styles from './MyTasksList.module.css';

const SEARCH_DEBOUNCE_MS = 300;

/** The planner as grouped lists: overdue, today, upcoming, no deadline (and completed). */
export function MyTasksList() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const filter = useMemo(() => listFilterFromParams(params), [params]);
  const active = listFilterActive(filter);
  const tasks = useMyTasks(filter);
  const boards = useMyBoards({ limit: 1000 });
  const workspaces = useWorkspaces();

  const change = (update: (current: ListFilter) => ListFilter) => {
    const next = listFilterToParams(update(filter));
    // The page's own parameters (`view`) and the calendar's stay untouched.
    for (const key of ['view', 'cal', 'date', 'cd']) {
      const value = params.get(key);
      if (value !== null) next.set(key, value);
    }
    setParams(next, { replace: true });
  };

  const [text, setText] = useState(filter.q);
  const [prevQ, setPrevQ] = useState(filter.q);
  if (filter.q !== prevQ) {
    setPrevQ(filter.q);
    setText(filter.q);
  }
  useEffect(() => {
    if (text === filter.q) return;
    const timer = setTimeout(() => {
      change((current) => ({ ...current, q: text }));
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
    // `change` is rebuilt every render from the latest URL: depend on what it reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, filter, params]);

  const data = tasks.data;
  // Labels are per board: match them by name (and color for nameless ones) across boards.
  const labelKey = (label: Pick<Label, 'name' | 'color'>) =>
    (label.name.trim() || label.color).toLocaleLowerCase();
  const labelById = useMemo(
    () => new Map((data?.labels ?? []).map((label) => [label.id, label])),
    [data?.labels],
  );
  const visible = useMemo(() => {
    const cards = data?.cards ?? [];
    return cards.filter((card) => {
      if (
        filter.labelNames.length > 0 &&
        !card.label_ids.some((id) => {
          const label = labelById.get(id);
          return label !== undefined && filter.labelNames.includes(labelKey(label));
        })
      ) {
        return false;
      }
      if (
        filter.assigneeIds.length > 0 &&
        !card.assignee_ids.some((id) => filter.assigneeIds.includes(id))
      ) {
        return false;
      }
      return true;
    });
  }, [data?.cards, labelById, filter.labelNames, filter.assigneeIds]);
  const groups = useMemo(() => groupTasks(visible), [visible]);
  const sources = useMemo(
    () => new Map<string, MyTaskBoard>((data?.boards ?? []).map((board) => [board.id, board])),
    [data?.boards],
  );
  const people = useMemo(
    () => new Map<string, MyTaskPerson>((data?.people ?? []).map((p) => [p.user_id, p])),
    [data?.people],
  );

  const boardOptions: FilterOption<string>[] = (boards.data ?? []).map((board) => ({
    value: board.id,
    label: board.name,
  }));
  const workspaceOptions: FilterOption<string>[] = (workspaces.data ?? []).map((workspace) => ({
    value: workspace.id,
    label: workspace.name,
  }));
  const labelOptions: FilterOption<string>[] = useMemo(() => {
    const seen = new Map<string, string>();
    for (const label of data?.labels ?? [])
      seen.set(labelKey(label), label.name.trim() || label.color);
    return [...seen].map(([value, label]) => ({ value, label }));
  }, [data?.labels]);
  const personOptions: FilterOption<string>[] = (data?.people ?? []).map((person) => ({
    value: person.user_id,
    label: person.display_name,
  }));

  const toggle = (list: string[], value: string) =>
    list.includes(value) ? list.filter((x) => x !== value) : [...list, value];

  const dueOptions: FilterOption<'overdue' | 'today' | 'upcoming' | 'none'>[] = [
    { value: 'overdue', label: t('myTasks.groups.overdue') },
    { value: 'today', label: t('myTasks.groups.today') },
    { value: 'upcoming', label: t('myTasks.groups.upcoming') },
    { value: 'none', label: t('myTasks.groups.none') },
  ];
  const statusOptions: FilterOption<'open' | 'completed'>[] = [
    { value: 'open', label: t('filter.statusOpen') },
    { value: 'completed', label: t('filter.statusDone') },
  ];
  const statusSelected: ('open' | 'completed')[] =
    filter.status === 'all' ? ['open', 'completed'] : [filter.status];

  return (
    <div className={styles.list}>
      <div className={styles.filters} role="search" aria-label={t('myTasks.filters')}>
        <input
          type="search"
          className={styles.search}
          aria-label={t('myTasks.search')}
          placeholder={t('myTasks.search')}
          value={text}
          maxLength={200}
          onChange={(event) => {
            setText(event.target.value);
          }}
        />
        <OptionFilter
          label={t('filter.status')}
          withAll={false}
          options={statusOptions}
          selected={statusSelected}
          onChange={() => undefined}
          onToggle={(option) => {
            // open (default), completed, or both - never an empty selection
            change((current) => {
              const has = (name: 'open' | 'completed') =>
                current.status === 'all' || current.status === name;
              const open = option === 'open' ? !has('open') : has('open');
              const done = option === 'completed' ? !has('completed') : has('completed');
              const status = open && done ? 'all' : done ? 'completed' : 'open';
              return { ...current, status };
            });
          }}
        />
        <OptionFilter
          label={t('myTasks.due')}
          options={dueOptions}
          selected={filter.buckets}
          onChange={(next) => {
            change((current) => ({ ...current, buckets: next }));
          }}
          onToggle={(option) => {
            change((current) => ({
              ...current,
              buckets: current.buckets.includes(option)
                ? current.buckets.filter((x) => x !== option)
                : [...current.buckets, option],
            }));
          }}
        />
        {workspaceOptions.length > 1 && (
          <OptionFilter
            label={t('myTasks.workspace')}
            options={workspaceOptions}
            selected={filter.workspaceIds}
            onChange={(next) => {
              change((current) => ({ ...current, workspaceIds: next }));
            }}
            onToggle={(option) => {
              change((current) => ({
                ...current,
                workspaceIds: toggle(current.workspaceIds, option),
              }));
            }}
          />
        )}
        {boardOptions.length > 1 && (
          <OptionFilter
            label={t('myTasks.board')}
            options={boardOptions}
            selected={filter.boardIds}
            onChange={(next) => {
              change((current) => ({ ...current, boardIds: next }));
            }}
            onToggle={(option) => {
              change((current) => ({ ...current, boardIds: toggle(current.boardIds, option) }));
            }}
          />
        )}
        {labelOptions.length > 0 && (
          <OptionFilter
            label={t('myTasks.labels')}
            options={labelOptions}
            selected={filter.labelNames}
            onChange={(next) => {
              change((current) => ({ ...current, labelNames: next }));
            }}
            onToggle={(option) => {
              change((current) => ({
                ...current,
                labelNames: toggle(current.labelNames, option),
              }));
            }}
          />
        )}
        {personOptions.length > 0 && (
          <OptionFilter
            label={t('myTasks.assignees')}
            options={personOptions}
            selected={filter.assigneeIds}
            onChange={(next) => {
              change((current) => ({ ...current, assigneeIds: next }));
            }}
            onToggle={(option) => {
              change((current) => ({
                ...current,
                assigneeIds: toggle(current.assigneeIds, option),
              }));
            }}
          />
        )}
        {active && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              change(() => EMPTY_LIST_FILTER);
              setText('');
            }}
          >
            {t('filter.resetFilters')}
          </Button>
        )}
      </div>

      {tasks.isPending && <LoadingState />}
      {tasks.isError && <ErrorState error={tasks.error} onRetry={() => void tasks.refetch()} />}
      {data && (
        <>
          {data.truncated && (
            <p className={styles.note} role="status">
              {t('myTasks.truncated')}
            </p>
          )}
          {visible.length === 0 ? (
            <EmptyState title={active ? t('myTasks.nothingMatches') : t('myTasks.empty')}>
              {!active && <p className={styles.note}>{t('myTasks.emptyHint')}</p>}
            </EmptyState>
          ) : (
            TASK_GROUPS.filter((group) => groups[group].length > 0).map((group) => (
              <TaskSection
                key={group}
                group={group}
                cards={groups[group]}
                sources={sources}
                people={people}
                labelById={labelById}
              />
            ))
          )}
        </>
      )}
    </div>
  );
}

function TaskSection({
  group,
  cards,
  sources,
  people,
  labelById,
}: {
  group: TaskGroup;
  cards: Card[];
  sources: ReadonlyMap<string, MyTaskBoard>;
  people: ReadonlyMap<string, MyTaskPerson>;
  labelById: ReadonlyMap<string, Label>;
}) {
  const { t } = useTranslation();
  const headingId = `my-tasks-${group}`;
  return (
    <section className={styles.section} aria-labelledby={headingId} data-group={group}>
      <h2 id={headingId} className={styles.heading}>
        {t(`myTasks.groups.${group}`)} <span className={styles.count}>{cards.length}</span>
      </h2>
      <ul className={styles.rows}>
        {cards.map((card) => {
          const source = sources.get(card.placement.board_id);
          const names = card.assignee_ids.flatMap((id) => people.get(id)?.display_name ?? []);
          return (
            <li key={card.id} className={styles.row} data-testid="my-task">
              <Link to={taskPath(card.placement.board_id, card.id)} className={styles.title}>
                {card.title}
              </Link>
              <span className={styles.meta}>
                {source && (
                  <span className={styles.source}>
                    {[source.name, source.workspace_name].filter(Boolean).join(' · ')}
                  </span>
                )}
                <DeadlineBadge card={card} />
                {names.length > 0 && <span className={styles.people}>{names.join(', ')}</span>}
                {card.label_ids.map((id) => {
                  const label = labelById.get(id);
                  return label ? (
                    <span
                      key={id}
                      className={styles.label}
                      style={{ borderColor: label.color }}
                      title={label.name}
                    >
                      <span
                        aria-hidden
                        className={styles.dot}
                        style={{ background: label.color }}
                      />
                      {label.name}
                    </span>
                  ) : null;
                })}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
